import {
  ABRIDGED_RETIRED_MESSAGE,
  DEFAULT_OUTPUT_PROFILE,
  outputProfileOfTypeName,
  type OutputProfile,
} from '@/core/outputProfile';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import { SubmissionStatusId, type SubmissionRow } from '@/core/models/filing.models';
import { ReportTypeCode } from '@/core/models/reports.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import { reportsService } from '@/core/services/reports';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { SelectionBar } from '@/kit/SelectionBar';
import { openDialog } from '@/kit/dialog';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { isSubmissionFor, reportLabel } from './filingRules';
import { SubmissionResponse, type SubmissionResponseData, type SubmissionResponseResult } from './SubmissionResponse';
import { SubmitAccounts, type SubmitAccountsDialogData } from './SubmitAccounts';
import { submitListPath, useBusy, useClientGuard } from './shared';
import './TaxreturnTaskSubmit.scss';

/** #168: the generated annual accounts the preparer chooses from when starting a submission. */
interface ReportChoice {
  id: string;
  periodId: string;
  refNo: string;
  label: string;
  outputProfile: OutputProfile;
}

export function statusClass(row: SubmissionRow): string {
  if (row.statusId === SubmissionStatusId.Rejected || row.statusId === SubmissionStatusId.Failed) {
    return 'text-red';
  }
  return row.statusId === SubmissionStatusId.Accepted ? 'text-primary' : '';
}

/**
 * The Status column. As in Capium 3.0, a status with something behind it is a link: a pending one updates itself from
 * Companies House, and a refused or failed one shows the response. Draft and accepted stay plain text.
 */
function StatusCell({
  row,
  refreshing,
  onRefresh,
  onResponse,
}: {
  row: SubmissionRow;
  refreshing: boolean;
  onRefresh: () => void;
  onResponse: () => void;
}) {
  if (row.statusId === SubmissionStatusId.Pending || row.statusId === SubmissionStatusId.Submitting) {
    return (
      <a
        role="button"
        tabIndex={0}
        className="link"
        title="Click to update the status from Companies House"
        aria-busy={refreshing}
        onClick={onRefresh}
        onKeyDown={(event) => event.key === 'Enter' && onRefresh()}
      >
        {refreshing ? 'Updating...' : row.status}
      </a>
    );
  }
  if (row.statusId === SubmissionStatusId.Rejected || row.statusId === SubmissionStatusId.Failed) {
    return (
      <a
        role="button"
        tabIndex={0}
        className={`link ${statusClass(row)}`.trim()}
        title="Click to see the response"
        onClick={onResponse}
        onKeyDown={(event) => event.key === 'Enter' && onResponse()}
      >
        {row.status}
      </a>
    );
  }
  return <span className={statusClass(row)}>{row.status}</span>;
}

/** A submission that went to Companies House and came back refused or failed: it is sent again with Retry. */
const isRetryable = (row: SubmissionRow) =>
  row.canSubmit && (row.statusId === SubmissionStatusId.Rejected || row.statusId === SubmissionStatusId.Failed);

/**
 * The more_vert menu of a row: Submit (or Retry), Download response, SMS - the old Submit History's action list. The response
 * is the PDF of the attempts, which is what the download gives for accounts made in Capium; an imported iXBRL file's download
 * is the file itself, so it has no separate response item.
 */
function RowMenu({
  row,
  onSubmit,
  onDownload,
  onSms,
}: {
  row: SubmissionRow;
  onSubmit: () => void;
  onDownload: () => void;
  onSms: () => void;
}) {
  return (
    <>
      {row.canSubmit && (
        <Button title={isRetryable(row) ? 'Retry' : 'Submit to Companies House'} onClick={onSubmit}>
          <Icon name={isRetryable(row) ? 'refresh' : 'send'} outlined />
        </Button>
      )}
      {row.statusId !== SubmissionStatusId.Draft && !row.hasAttachment && (
        <Button title="Download response" onClick={onDownload}>
          <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
        </Button>
      )}
      <Button title="SMS" onClick={onSms}>
        <Icon name="sms" outlined />
      </Button>
    </>
  );
}

/**
 * "Submit Accounts" landing grid: the client's Companies House accounts submissions; start a new one from a generated
 * Annual Accounts report, import an iXBRL file, open / download / delete a submission.
 */
export function TaxreturnTaskSubmit() {
  const navigate = useNavigate();
  const client = useClientGuard();
  const creating = useBusy();
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const [dataSource, setDataSource] = useState<SubmissionRow[]>([]);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());

  const [reportChoices, setReportChoices] = useState<ReportChoice[]>([]);
  const [chosenReportId, setChosenReportId] = useState<string | null>(null);
  const [isChoosingReport, setIsChoosingReport] = useState(false);

  const load = () => {
    if (!client) {
      return;
    }
    filingService.getSubmissions(client.id).then(
      (rows) => {
        setDataSource(rows);
        const ids = new Set(rows.map((r) => r.id));
        setSelection((current) => new Set([...current].filter((id) => ids.has(id))));
      },
      (error) => notifyError(error),
    );
  };

  useEffect(load, [client]);

  /**
   * #168: lists the client's generated annual accounts to pick from; the submission is made for the chosen report's
   * accounting period (reopening the period's open submission rather than adding another).
   */
  const onAddnewReport = () => {
    if (!client || creating.isBusy) {
      return;
    }
    Promise.all([
      reportsService.getReports(client.id, ReportTypeCode.Annual),
      reportsService.getTrialBalances(client.id),
    ]).then(
      ([reports, trialBalances]) => {
        const periodOf = new Map(trialBalances.map((t) => [t.id, t.accountingPeriodId]));
        // APT341: Abridged accounts can no longer be filed (Companies House stops accepting them from 1 April 2027, for any period), so an
        // earlier Abridged report is not offered for submission; Full and Filleted are.
        const hasAbridged = reports.some((r) => outputProfileOfTypeName(r.type) === 'ABRIDGED' && r.canProduce !== false);
        const choices = reports
          // The client's Filleted reports are listed as they are produced; the company-size rules are enforced when the accounts are
          // generated. Full stays in the list because the filing flow supports it.
          .filter(
            (r) =>
              r.canProduce !== false &&
              outputProfileOfTypeName(r.type) !== 'ABRIDGED' &&
              !!periodOf.get(r.currentTrialBalanceId),
          )
          .map((r) => ({
            id: r.id,
            periodId: periodOf.get(r.currentTrialBalanceId)!,
            refNo: r.refNo,
            label: reportLabel(r),
            outputProfile: outputProfileOfTypeName(r.type),
          }));
        setReportChoices(choices);
        if (choices.length === 0) {
          notifyError(
            hasAbridged
              ? `${ABRIDGED_RETIRED_MESSAGE} Generate them under Reports > Annual Accounts.`
              : 'There are no accounts to submit yet. ' +
                  'Generate them under Reports > Annual Accounts from a trial balance linked to an accounting period.',
          );
          return;
        }
        setChosenReportId(null);
        setIsChoosingReport(true);
      },
      (error) => notifyError(error),
    );
  };

  /**
   * #168: starts (or reopens) the submission for the chosen report. Only a submission started for this report is
   * reopened - another report's submission for the same period is never taken for it.
   */
  const submitChosenReport = () => {
    const chosen = reportChoices.find((r) => r.id === chosenReportId);
    if (!client || !chosen || creating.isBusy) {
      notifyError('Select the accounts to submit.');
      return;
    }
    if (!creating.begin()) {
      return;
    }
    const clientId = client.id;
    filingService
      .getSubmissions(clientId)
      .then((submissions) => {
        const open = submissions.find(
          (s) => s.accountingPeriodId === chosen.periodId && s.canSubmit && isSubmissionFor(s, chosen.refNo),
        );
        return open
          ? open.id
          : filingService
              .createSubmission(
                clientId,
                chosen.periodId,
                `Accounts ${chosen.label}`,
                // The Abridged or Filleted accounts of the chosen report are the ones filed; Full is the default.
                ...(chosen.outputProfile === DEFAULT_OUTPUT_PROFILE ? [] : [chosen.outputProfile]),
              )
              .then((created) => created.id);
      })
      .then(
        (id) => {
          setIsChoosingReport(false);
          void navigate(submitListPath(id));
        },
        (error) => notifyError(error),
      )
      .finally(creating.end);
  };

  const cancelChooseReport = () => {
    setIsChoosingReport(false);
    setChosenReportId(null);
  };

  const clickSubmitAccounts = () => {
    if (!client) {
      return;
    }
    openDialog<SubmitAccountsDialogData, boolean>(SubmitAccounts, { data: { clientId: client.id } })
      .afterClosed()
      .then((saved) => {
        if (saved) {
          notifySuccess(NotificationTextMessage.successMessage);
          load();
        }
      });
  };

  const openSubmission = (row: SubmissionRow) => void navigate(submitListPath(row.id));

  /** Capium 3.0: clicking a pending status asks Companies House for the decision and updates it. */
  const refreshStatus = (row: SubmissionRow) => {
    if (refreshingId) {
      return;
    }
    setRefreshingId(row.id);
    filingService
      .refreshSubmissionStatus(row.id)
      .then(
        (saved) => {
          if (saved.statusId === row.statusId) {
            notifySuccess(`${row.refNo} is still ${saved.status.toLowerCase()}: Companies House has not decided yet.`);
          } else {
            notifySuccess(`${row.refNo} is now ${saved.status.toLowerCase()}.`);
          }
          load();
        },
        (error) => notifyError(error),
      )
      .finally(() => setRefreshingId(null));
  };

  /** Capium 3.0: clicking ERROR showed the response; here that is the response panel, which can also retry. */
  const showResponse = (row: SubmissionRow) => {
    void openDialog<SubmissionResponseData, SubmissionResponseResult>(SubmissionResponse, {
      data: { id: row.id, refNo: row.refNo },
    })
      .afterClosed()
      .then((result) => {
        if (result === 'retry') {
          openSubmission(row);
        }
      });
  };

  const download = (row: SubmissionRow) => {
    filingService.downloadSubmission(row.id).then(undefined, (error) => notifyError(error));
  };

  const sendSms = (row: SubmissionRow) => {
    notifyError(
      `SMS notifications are not available for ${row.refNo}: no SMS provider is configured for Accounts Production.`,
    );
  };

  const openDeleteClick = (row: SubmissionRow) => {
    if (!row.canDelete) {
      notifyError('A submission that has been sent to Companies House cannot be deleted.');
      return;
    }
    // The ticked rows when this row is one of them; submissions already sent are left alone.
    if (selection.has(row.id) && selection.size > 1) deleteSelected();
    else confirmDelete([row.id], 0);
  };

  /** The ticked submissions that can still be deleted; those already sent to Companies House are kept (and counted). */
  const deleteSelected = () => {
    const ids = dataSource.filter((r) => selection.has(r.id) && r.canDelete).map((r) => r.id);
    if (ids.length === 0) {
      notifyError('A submission that has been sent to Companies House cannot be deleted.');
      return;
    }
    confirmDelete(ids, selection.size - ids.length);
  };

  const confirmDelete = (ids: string[], skipped: number) => {
    openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 ? 'Submissions' : 'Submission', count: ids.length },
    })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        Promise.all(ids.map((id) => filingService.deleteSubmission(id))).then(
          () => {
            setSelection((current) => new Set([...current].filter((id) => !ids.includes(id))));
            notifySuccess(
              skipped > 0
                ? `${ids.length} deleted; ${skipped} already sent to Companies House were kept.`
                : NotificationTextMessage.recordDeletedSuccessfully,
            );
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  const isAllSelected = () => dataSource.length > 0 && dataSource.every((row) => selection.has(row.id));

  const toggleAll = (checked: boolean) => {
    setSelection(checked ? new Set(dataSource.map((row) => row.id)) : new Set());
  };

  const toggleRow = (row: SubmissionRow, checked: boolean) => {
    setSelection((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(row.id);
      } else {
        next.delete(row.id);
      }
      return next;
    });
  };

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-task-submit">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={onAddnewReport}>
              <Icon name="add" />
              <span>Submit Accounts</span>
            </Button>
          </div>
          <div className="content-header-right mb-1">
            <Button
              className="action-button primary-button"
              type="button"
              onClick={clickSubmitAccounts}
            >
              <Icon name="send" outlined />
              Submit Imported iXBRL Accounts
            </Button>
          </div>
        </div>

        {/* #168: choose which generated accounts to submit, rather than the latest period being picked. */}
        {isChoosingReport && (
          <div className="d-flex align-items-center gap-15 mb-1">
            <div className="form-field mb-0">
              <p>
                Accounts to submit<span className="text-red">*</span>
              </p>
              <Select<ReportChoice>
                className="w-510"
                items={reportChoices}
                bindLabel="label"
                bindValue="id"
                value={chosenReportId}
                placeholder="Select the accounts report"
                onChange={(value) => setChosenReportId((value as string | null) ?? null)}
              />
            </div>
            <Button
              className="action-button primary-button mt-1"
              type="button"
              disabled={!chosenReportId || creating.isBusy}
              onClick={submitChosenReport}
            >
              Continue
            </Button>
            <Button className="action-button secondary-button mt-1" type="button" onClick={cancelChooseReport}>
              Cancel
            </Button>
          </div>
        )}

        <SelectionBar count={selection.size} onDelete={deleteSelected} onClear={() => setSelection(new Set())} />
        <div className="mat-table-wrapper">
          <DataTable
            stickyHeader
            rows={dataSource}
            rowKey={(row) => row.id}
            columns={[
              {
                id: 'no',
                header: (
                  <>
                    <Checkbox
                      checked={isAllSelected()}
                      indeterminate={selection.size > 0 && !isAllSelected()}
                      onChange={(event) => toggleAll(event.target.checked)}
                    />
                    Ref. No.
                  </>
                ),
                cell: (row) => (
                  <>
                    <Checkbox
                      checked={selection.has(row.id)}
                      onChange={(event) => toggleRow(row, event.target.checked)}
                    />
                    <a className="link" onClick={() => openSubmission(row)}>
                      {row.refNo}
                    </a>
                  </>
                ),
              },
              { id: 'from', header: 'From', cell: (row) => row.from },
              { id: 'to', header: 'To', cell: (row) => row.to },
              { id: 'accounts', header: 'Accounts by', cell: (row) => row.accounts },
              { id: 'description', header: 'Description', cell: (row) => row.description },
              { id: 'submission', header: 'Submission Date', cell: (row) => row.submission },
              {
                id: 'status',
                header: 'Status',
                cell: (row) => (
                  <StatusCell
                    row={row}
                    refreshing={refreshingId === row.id}
                    onRefresh={() => refreshStatus(row)}
                    onResponse={() => showResponse(row)}
                  />
                ),
              },
              {
                id: 'actions',
                header: 'Action',
                cell: (row) => (
                  <div className="d-flex align-items-center grid-actions">
                    <Button title="Download" onClick={() => download(row)}>
                      <Icon name="file_download" outlined />
                    </Button>
                    <Button title="Delete" onClick={() => openDeleteClick(row)}>
                      <Icon name="delete_outline" outlined />
                    </Button>
                    <RowMenu
                      row={row}
                      onSubmit={() => openSubmission(row)}
                      onDownload={() => download(row)}
                      onSms={() => sendSms(row)}
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </SideMenuPage>
  );
}
